import React, { useState, useRef, useMemo } from "react";
import {
  Group,
  Button,
  Image,
  SimpleGrid,
  Text,
  Paper,
  Badge,
  ActionIcon,
  Tooltip,
  Box,
  Progress,
  Stack,
  Divider,
  Table,
  TextInput,
  SegmentedControl,
  Card,
  Alert,
  Modal,
  Textarea,
} from "@mantine/core";
import {
  IconUpload,
  IconFolder,
  IconPhoto,
  IconCopy,
  IconX,
  IconFileTypePdf,
  IconExternalLink,
  IconFileSpreadsheet,
  IconFileText,
  IconTrash,
  IconSearch,
  IconLayoutGrid,
  IconList,
  IconLink,
  IconMovie,
} from "@tabler/icons-react";
import * as xlsx from "xlsx";
import { uploadFile } from "../../api/ApiProduct";
import { NotificationExtension } from "../../_base/extension/NotificationExtension";
import { convertImageDomain } from "../../const/converhandle";

interface UploadedItem {
  url: string;
  pathNameBefore?: string;
  fileName?: string;
  originalName?: string;
  size?: number;
  relativePath?: string;
  [key: string]: any;
}

interface SelectedFileInfo {
  file: File;
  relativePath: string;
  previewUrl?: string;
  size: number;
}

const BATCH_SIZE = 15; // Số file upload mỗi lượt để tránh quá tải/timeout mạng

const isPdfFile = (urlOrName?: string): boolean => {
  if (!urlOrName) return false;
  const clean = urlOrName.split("?")[0].toLowerCase();
  return clean.endsWith(".pdf");
};

const isVideoFile = (urlOrName?: string): boolean => {
  if (!urlOrName) return false;
  const clean = urlOrName.split("?")[0].toLowerCase();
  return (
    clean.endsWith(".webm") ||
    clean.endsWith(".mp4") ||
    clean.endsWith(".ogg") ||
    clean.endsWith(".mov")
  );
};

const isSupportedFile = (file: File): boolean => {
  const lowerName = file.name.toLowerCase();
  return (
    file.type.startsWith("image/") ||
    file.type.startsWith("video/") ||
    file.type === "application/pdf" ||
    lowerName.endsWith(".jpg") ||
    lowerName.endsWith(".jpeg") ||
    lowerName.endsWith(".png") ||
    lowerName.endsWith(".webp") ||
    lowerName.endsWith(".gif") ||
    lowerName.endsWith(".svg") ||
    lowerName.endsWith(".pdf") ||
    lowerName.endsWith(".webm") ||
    lowerName.endsWith(".mp4") ||
    lowerName.endsWith(".ogg") ||
    lowerName.endsWith(".mov")
  );
};

const canCreatePreview = (file: File): boolean => {
  return (
    file.type.startsWith("image/") ||
    file.type.startsWith("video/") ||
    isVideoFile(file.name) ||
    file.name.toLowerCase().endsWith(".webp") ||
    file.name.toLowerCase().endsWith(".webm")
  );
};

const formatFileSize = (bytes?: number): string => {
  if (!bytes || bytes === 0) return "0 B";
  const k = 1024;
  const sizes = ["B", "KB", "MB", "GB"];
  const i = Math.floor(Math.log(bytes) / Math.log(k));
  return `${parseFloat((bytes / Math.pow(k, i)).toFixed(1))} ${sizes[i]}`;
};

const getFileUrl = (item: UploadedItem | string): string => {
  if (typeof item === "string") return item;
  return item?.url || "";
};

const getFileName = (item: UploadedItem | string): string => {
  if (typeof item === "string") return item.split("/").pop() || item;
  return (
    item?.relativePath ||
    item?.originalName ||
    item?.pathNameBefore ||
    item?.fileName ||
    (item?.url ? item.url.split("/").pop() : "Tệp tải lên") ||
    "Tệp tải lên"
  );
};

// Đệ quy quét file từ thư mục khi kéo thả (HTML5 FileSystem API)
const scanFilesFromEntry = async (
  entry: any,
  parentPath = ""
): Promise<SelectedFileInfo[]> => {
  if (!entry) return [];
  const currentPath = parentPath ? `${parentPath}/${entry.name}` : entry.name;

  if (entry.isFile) {
    return new Promise((resolve) => {
      entry.file(
        (file: File) => {
          if (isSupportedFile(file)) {
            resolve([
              {
                file,
                relativePath: currentPath,
                size: file.size,
                previewUrl: canCreatePreview(file)
                  ? URL.createObjectURL(file)
                  : undefined,
              },
            ]);
          } else {
            resolve([]);
          }
        },
        () => resolve([])
      );
    });
  }

  if (entry.isDirectory) {
    const dirReader = entry.createReader();
    const readAllEntries = async (): Promise<any[]> => {
      let entries: any[] = [];
      let read = true;
      while (read) {
        const batch: any[] = await new Promise((resolve) =>
          dirReader.readEntries(resolve, () => resolve([]))
        );
        if (batch.length > 0) {
          entries = entries.concat(batch);
        } else {
          read = false;
        }
      }
      return entries;
    };

    const entries = await readAllEntries();
    const results = await Promise.all(
      entries.map((childEntry) => scanFilesFromEntry(childEntry, currentPath))
    );
    return results.flat();
  }

  return [];
};

const UploadImageOnline = () => {
  const [selectedFiles, setSelectedFiles] = useState<SelectedFileInfo[]>([]);
  const [folderName, setFolderName] = useState<string>("");
  const [uploadedItems, setUploadedItems] = useState<UploadedItem[]>([]);
  const [loading, setLoading] = useState(false);
  const [uploadProgress, setUploadProgress] = useState<number>(0);
  const [uploadStatusText, setUploadStatusText] = useState<string>("");
  const [error, setError] = useState<string | null>(null);
  const [isDragOver, setIsDragOver] = useState(false);

  // Hiển thị kết quả
  const [viewMode, setViewMode] = useState<"grid" | "table">("grid");
  const [searchQuery, setSearchQuery] = useState<string>("");

  const folderInputRef = useRef<HTMLInputElement | null>(null);
  const fileInputRef = useRef<HTMLInputElement | null>(null);

  // Modal tải file từ URL
  const [urlModalOpened, setUrlModalOpened] = useState<boolean>(false);
  const [urlInput, setUrlInput] = useState<string>("");
  const [isFetchingUrl, setIsFetchingUrl] = useState<boolean>(false);
  const [urlFetchError, setUrlFetchError] = useState<string | null>(null);
  const [failedUrlsList, setFailedUrlsList] = useState<{ url: string; error: string }[]>([]);

  // Xử lý khi chọn từ input Folder
  const handleFolderSelect = (e: React.ChangeEvent<HTMLInputElement>) => {
    const fileList = e.target.files;
    if (!fileList || fileList.length === 0) return;

    const filesArray = Array.from(fileList);
    let detectedFolderName = "";

    const validFiles: SelectedFileInfo[] = [];
    filesArray.forEach((file) => {
      if (isSupportedFile(file)) {
        const relPath = (file as any).webkitRelativePath || file.name;
        if (!detectedFolderName && relPath.includes("/")) {
          detectedFolderName = relPath.split("/")[0];
        }
        validFiles.push({
          file,
          relativePath: relPath,
          size: file.size,
          previewUrl: canCreatePreview(file)
            ? URL.createObjectURL(file)
            : undefined,
        });
      }
    });

    if (detectedFolderName) {
      setFolderName(detectedFolderName);
    }
    setSelectedFiles(validFiles);
    setError(null);
    e.target.value = "";
  };

  // Xử lý khi chọn từ input File lẻ
  const handleFilesSelect = (e: React.ChangeEvent<HTMLInputElement>) => {
    const fileList = e.target.files;
    if (!fileList || fileList.length === 0) return;

    const filesArray = Array.from(fileList);
    const validFiles: SelectedFileInfo[] = filesArray
      .filter((file) => isSupportedFile(file))
      .map((file) => ({
        file,
        relativePath: file.name,
        size: file.size,
        previewUrl: canCreatePreview(file)
          ? URL.createObjectURL(file)
          : undefined,
      }));

    setSelectedFiles((prev) => [...prev, ...validFiles]);
    setError(null);
    e.target.value = "";
  };

  // Tải file từ đường dẫn URL (Ảnh/Video .webm/PDF)
  const handleFetchFromUrl = async () => {
    const raw = urlInput.trim();
    if (!raw) return;

    setIsFetchingUrl(true);
    setUrlFetchError(null);
    setFailedUrlsList([]);

    const urls = raw
      .split(/\r?\n/)
      .map((u) => u.trim())
      .filter((u) => Boolean(u) && (u.startsWith("http://") || u.startsWith("https://")));

    if (urls.length === 0) {
      setUrlFetchError("Vui lòng nhập ít nhất một đường dẫn hợp lệ (bắt đầu bằng http:// hoặc https://)");
      setIsFetchingUrl(false);
      return;
    }

    const fetchedFiles: SelectedFileInfo[] = [];
    const failedUrls: { url: string; error: string }[] = [];

    for (const url of urls) {
      try {
        const response = await fetch(url);
        if (!response.ok) {
          throw new Error(`Mã phản hồi HTTP: ${response.status}`);
        }
        const blob = await response.blob();

        const urlObj = new URL(url);
        const pathParts = urlObj.pathname.split("/").filter(Boolean);
        let rawFileName = pathParts.length > 0 ? decodeURIComponent(pathParts[pathParts.length - 1]) : "file";

        if (!rawFileName.includes(".")) {
          if (blob.type.includes("webm")) rawFileName += ".webm";
          else if (blob.type.includes("mp4")) rawFileName += ".mp4";
          else if (blob.type.includes("png")) rawFileName += ".png";
          else if (blob.type.includes("jpeg")) rawFileName += ".jpg";
          else if (blob.type.includes("webp")) rawFileName += ".webp";
          else if (blob.type.includes("gif")) rawFileName += ".gif";
          else if (blob.type.includes("pdf")) rawFileName += ".pdf";
        }

        const file = new File([blob], rawFileName, {
          type: blob.type || (isVideoFile(rawFileName) ? "video/webm" : "application/octet-stream"),
        });

        if (isSupportedFile(file)) {
          fetchedFiles.push({
            file,
            relativePath: rawFileName,
            size: file.size,
            previewUrl: canCreatePreview(file) ? URL.createObjectURL(file) : undefined,
          });
        } else {
          failedUrls.push({
            url,
            error: `Định dạng "${blob.type || "không xác định"}" chưa được hỗ trợ`,
          });
        }
      } catch (err: any) {
        failedUrls.push({
          url,
          error: "Bị server nguồn chặn CORS từ trình duyệt (Failed to fetch)",
        });
      }
    }

    if (fetchedFiles.length > 0) {
      setSelectedFiles((prev) => [...prev, ...fetchedFiles]);
      NotificationExtension.Success(
        `Đã tải thành công ${fetchedFiles.length} tệp từ liên kết URL vào danh sách chờ!`
      );
    }

    if (failedUrls.length > 0) {
      setFailedUrlsList(failedUrls);
      setUrlFetchError(
        `Có ${failedUrls.length} liên kết không thể tải tự động do server nguồn chặn CORS bảo mật trình duyệt.`
      );
      setIsFetchingUrl(false);
    } else {
      setIsFetchingUrl(false);
      setUrlInput("");
      setFailedUrlsList([]);
      setUrlModalOpened(false);
    }
  };

  // Xử lý kéo thả Dropzone (hỗ trợ cả Folder và File)
  const handleDrop = async (e: React.DragEvent<HTMLDivElement>) => {
    e.preventDefault();
    setIsDragOver(false);

    const items = e.dataTransfer.items;
    if (!items || items.length === 0) return;

    const scannedPromises: Promise<SelectedFileInfo[]>[] = [];
    let detectedFolder = "";

    for (let i = 0; i < items.length; i++) {
      const item = items[i];
      if (item.webkitGetAsEntry) {
        const entry = item.webkitGetAsEntry();
        if (entry) {
          if (entry.isDirectory && !detectedFolder) {
            detectedFolder = entry.name;
          }
          scannedPromises.push(scanFilesFromEntry(entry));
        }
      } else {
        const file = item.getAsFile();
        if (file && isSupportedFile(file)) {
          scannedPromises.push(
            Promise.resolve([
              {
                file,
                relativePath: file.name,
                size: file.size,
                previewUrl: canCreatePreview(file)
                  ? URL.createObjectURL(file)
                  : undefined,
              },
            ])
          );
        }
      }
    }

    try {
      const allResults = await Promise.all(scannedPromises);
      const flattened = allResults.flat();
      if (flattened.length === 0) {
        setError("Không tìm thấy tệp hình ảnh, video (.webm, .mp4) hoặc PDF hợp lệ trong tệp/thư mục đã thả.");
        return;
      }
      if (detectedFolder) {
        setFolderName(detectedFolder);
      }
      setSelectedFiles((prev) => [...prev, ...flattened]);
      setError(null);
    } catch {
      setError("Có lỗi xảy ra khi đọc tệp từ thư mục thả vào.");
    }
  };

  // Xóa 1 file trong hàng đợi
  const handleRemoveFile = (index: number) => {
    setSelectedFiles((prev) => {
      const item = prev[index];
      if (item?.previewUrl) URL.revokeObjectURL(item.previewUrl);
      return prev.filter((_, i) => i !== index);
    });
  };

  // Xóa toàn bộ hàng đợi
  const handleClearSelected = () => {
    selectedFiles.forEach((f) => {
      if (f.previewUrl) URL.revokeObjectURL(f.previewUrl);
    });
    setSelectedFiles([]);
    setFolderName("");
    setError(null);
  };

  // Upload danh sách file lên CDN theo từng Batch
  const handleUpload = async () => {
    if (!selectedFiles.length) {
      setError("Vui lòng chọn ít nhất một hình ảnh hoặc thư mục để tải lên.");
      return;
    }

    setLoading(true);
    setError(null);
    setUploadProgress(0);

    const totalFiles = selectedFiles.length;
    const batchCount = Math.ceil(totalFiles / BATCH_SIZE);
    const successfullyUploaded: UploadedItem[] = [];
    let failedCount = 0;

    try {
      for (let b = 0; b < batchCount; b++) {
        const start = b * BATCH_SIZE;
        const currentBatch = selectedFiles.slice(start, start + BATCH_SIZE);
        const filesToUpload = currentBatch.map((item) => item.file);

        setUploadStatusText(
          `Đang tải lên đợt ${b + 1}/${batchCount} (${Math.min(
            start + BATCH_SIZE,
            totalFiles
          )}/${totalFiles} tệp)...`
        );

        try {
          const response = await uploadFile(filesToUpload, "productDetail");
          if (response && response.success && Array.isArray(response.data)) {
            // Gán thêm thông tin tên gốc và relative path cho từng item
            const mapped = response.data.map((resItem, idx) => {
              const matchedOrigin = currentBatch[idx];
              return {
                ...resItem,
                originalName: matchedOrigin?.file?.name || "",
                relativePath: matchedOrigin?.relativePath || "",
                size: matchedOrigin?.size || 0,
              };
            });
            successfullyUploaded.push(...mapped);
          } else {
            failedCount += currentBatch.length;
          }
        } catch {
          failedCount += currentBatch.length;
        }

        const currentUploadedTotal = Math.min((b + 1) * BATCH_SIZE, totalFiles);
        setUploadProgress(Math.round((currentUploadedTotal / totalFiles) * 100));
      }

      if (successfullyUploaded.length > 0) {
        setUploadedItems((prev) => [...successfullyUploaded, ...prev]);
        NotificationExtension.Success(
          `Đã tải lên thành công ${successfullyUploaded.length}/${totalFiles} tệp lên CDN!`
        );
        handleClearSelected();
      }

      if (failedCount > 0) {
        setError(
          `Có ${failedCount} tệp không thể tải lên. Vui lòng kiểm tra lại dung lượng hoặc kết nối mạng.`
        );
      }
    } catch {
      setError("Có lỗi xảy ra trong quá trình tải lên CDN.");
    } finally {
      setLoading(false);
      setUploadStatusText("");
    }
  };

  // 1. Xuất file Excel (.xlsx) chứa toàn bộ danh sách CDN
  const handleExportExcel = () => {
    if (!uploadedItems.length) {
      NotificationExtension.Warn("Chưa có danh sách CDN để xuất file!");
      return;
    }

    const exportRows = uploadedItems.map((item, index) => {
      const rawUrl = getFileUrl(item);
      const cdnUrl = convertImageDomain(rawUrl);
      const fileName = getFileName(item);

      return {
        STT: index + 1,
        "Tên tệp / Đường dẫn thư mục": fileName,
        "Đường dẫn CDN hoàn chỉnh": cdnUrl,
        "Đường dẫn CDN gốc": rawUrl,
        "Dung lượng": formatFileSize(item.size),
        "Trạng thái": "Thành công",
      };
    });

    const worksheet = xlsx.utils.json_to_sheet(exportRows);

    // Tự động điều chỉnh độ rộng cột
    worksheet["!cols"] = [
      { wch: 6 }, // STT
      { wch: 35 }, // Tên tệp
      { wch: 60 }, // CDN hoàn chỉnh
      { wch: 50 }, // CDN gốc
      { wch: 14 }, // Dung lượng
      { wch: 14 }, // Trạng thái
    ];

    const workbook = xlsx.utils.book_new();
    xlsx.utils.book_append_sheet(workbook, worksheet, "Danh sách CDN");

    const now = new Date();
    const dateStr = `${now.getFullYear()}${String(now.getMonth() + 1).padStart(2, "0")}${String(
      now.getDate()
    ).padStart(2, "0")}_${String(now.getHours()).padStart(2, "0")}${String(
      now.getMinutes()
    ).padStart(2, "0")}`;

    const fileNameOutput = folderName
      ? `CDN_${folderName}_${dateStr}.xlsx`
      : `Danh_Sach_CDN_Upload_${dateStr}.xlsx`;

    xlsx.writeFile(workbook, fileNameOutput);
    NotificationExtension.Success("Đã xuất file Excel danh sách CDN thành công!");
  };

  // 2. Xuất file Text (.txt) chứa toàn bộ CDN URL
  const handleExportTxt = () => {
    if (!uploadedItems.length) {
      NotificationExtension.Warn("Chưa có danh sách CDN để xuất file!");
      return;
    }

    const lines = uploadedItems.map((item) => {
      const cdnUrl = convertImageDomain(getFileUrl(item));
      return cdnUrl;
    });

    const content = lines.join("\n");
    const blob = new Blob([content], { type: "text/plain;charset=utf-8" });
    const url = URL.createObjectURL(blob);
    const link = document.createElement("a");
    link.href = url;
    link.download = folderName
      ? `CDN_${folderName}_Links.txt`
      : `Danh_Sach_CDN_Links.txt`;
    link.click();
    URL.revokeObjectURL(url);
    NotificationExtension.Success("Đã tải file Text chứa danh sách CDN!");
  };

  // 3. Sao chép toàn bộ CDN URL vào Clipboard
  const handleCopyAllUrls = async () => {
    if (!uploadedItems.length) return;
    const allUrls = uploadedItems
      .map((item) => convertImageDomain(getFileUrl(item)))
      .join("\n");

    try {
      await navigator.clipboard.writeText(allUrls);
      NotificationExtension.Success(
        `Đã sao chép toàn bộ ${uploadedItems.length} đường dẫn CDN vào bộ nhớ tạm!`
      );
    } catch {
      NotificationExtension.Fails("Không thể sao chép vào bộ nhớ tạm.");
    }
  };

  // Bộ lọc tìm kiếm kết quả
  const filteredItems = useMemo(() => {
    if (!searchQuery.trim()) return uploadedItems;
    const q = searchQuery.toLowerCase().trim();
    return uploadedItems.filter((item) => {
      const name = getFileName(item).toLowerCase();
      const url = getFileUrl(item).toLowerCase();
      return name.includes(q) || url.includes(q);
    });
  }, [uploadedItems, searchQuery]);

  return (
    <Box style={{ maxWidth: 1050, margin: "24px auto", padding: "0 16px" }}>
      {/* Ẩn các native input để trigger bằng nút đẹp */}
      <input
        type="file"
        ref={folderInputRef}
        style={{ display: "none" }}
        /* @ts-ignore */
        webkitdirectory=""
        directory=""
        multiple
        onChange={handleFolderSelect}
      />
      <input
        type="file"
        ref={fileInputRef}
        style={{ display: "none" }}
        multiple
        accept="image/*,application/pdf,.pdf,video/webm,.webm,video/mp4,.mp4,video/*"
        onChange={handleFilesSelect}
      />

      {/* Modal dán URL */}
      <Modal
        opened={urlModalOpened}
        onClose={() => {
          if (!isFetchingUrl) {
            setUrlModalOpened(false);
            setUrlFetchError(null);
          }
        }}
        title={
          <Group gap="xs">
            <IconLink size={20} color="#1c7ed6" />
            <Text fw={700} size="md">
              Tải tệp lên từ liên kết URL (Ảnh / Video .webm / PDF)
            </Text>
          </Group>
        }
        size="lg"
        radius="md"
        centered
      >
        <Stack gap="sm">
          <Text size="xs" c="dimmed">
            Dán đường dẫn ảnh hoặc video (.webm, .mp4) trực tiếp từ website vào đây (hỗ trợ nhập nhiều link, mỗi link một dòng):
          </Text>

          <Textarea
            placeholder="https://www.buildcores.com/images/landing/v3/hero/pass3-no-fade-seam-1440-transparent.v2.webm&#10;https://example.com/banner.png"
            minRows={4}
            maxRows={8}
            autosize
            value={urlInput}
            onChange={(e) => setUrlInput(e.target.value)}
            disabled={isFetchingUrl}
          />

          {failedUrlsList.length > 0 && (
            <Alert icon={<IconX size={16} />} color="red" radius="md">
              <Text size="xs" fw={700} mb={6} c="red.8">
                Không thể tải trực tiếp do website nguồn chặn CORS từ trình duyệt:
              </Text>
              <Stack gap={6} mb="xs">
                {failedUrlsList.map((item, idx) => (
                  <Paper
                    key={idx}
                    p={8}
                    withBorder
                    radius="sm"
                    style={{ backgroundColor: "#fff" }}
                  >
                    <Group justify="space-between" align="center" wrap="nowrap">
                      <Box style={{ overflow: "hidden", flex: 1, marginRight: 8 }}>
                        <Text size="xs" fw={600} truncate title={item.url}>
                          {item.url}
                        </Text>
                        <Text size="11px" c="red.6">
                          {item.error}
                        </Text>
                      </Box>
                      <Button
                        size="xs"
                        variant="filled"
                        color="blue"
                        component="a"
                        href={item.url}
                        target="_blank"
                        rel="noopener noreferrer"
                        leftSection={<IconExternalLink size={14} />}
                        style={{ flexShrink: 0 }}
                      >
                        Mở link tải về máy
                      </Button>
                    </Group>
                  </Paper>
                ))}
              </Stack>
              <Text size="xs" c="dark.7" style={{ lineHeight: 1.5 }}>
                👉 <strong>Cách xử lý:</strong> Bấm nút <strong>"Mở link tải về máy"</strong> ở trên, tại tab mới bấm <strong>Chuột phải</strong> vào video chọn <em>"Lưu video thành..." (Save video as)</em> để tải về máy tính. Sau đó đóng popup này và kéo thả file <code>.webm</code> vào ô tải lên.
              </Text>
            </Alert>
          )}

          {urlFetchError && failedUrlsList.length === 0 && (
            <Alert
              icon={<IconX size={16} />}
              color="red"
              radius="md"
              style={{ whiteSpace: "pre-wrap", fontSize: 13 }}
            >
              {urlFetchError}
            </Alert>
          )}

          <Group justify="flex-end" mt="xs">
            <Button
              variant="default"
              onClick={() => {
                setUrlModalOpened(false);
                setUrlFetchError(null);
              }}
              disabled={isFetchingUrl}
            >
              Hủy
            </Button>
            <Button
              leftSection={<IconUpload size={16} />}
              color="blue"
              onClick={handleFetchFromUrl}
              loading={isFetchingUrl}
              disabled={!urlInput.trim()}
            >
              Tải về & Đưa vào danh sách
            </Button>
          </Group>
        </Stack>
      </Modal>

      {/* Card Header & Khu vực chọn / kéo thả */}
      <Paper
        shadow="xs"
        p="xl"
        radius="lg"
        style={{
          background: "#fff",
          border: "1px solid #e9ecef",
        }}
        mb="xl"
      >
        <Stack gap="xs" mb="lg">
          <Group justify="space-between" align="flex-start" wrap="wrap">
            <Box>
              <Text size="xl" fw={700} c="dark.8">
                Tải lên Thư mục Ảnh & Nhận Danh sách CDN
              </Text>
              <Text size="sm" c="dimmed" mt={2}>
                Tải lên nguyên cả thư mục hoặc nhiều ảnh, video WEBM/MP4, PDF cùng lúc, hệ thống sẽ lưu lên CDN và hỗ trợ bạn xuất toàn bộ đường dẫn ra Excel/Text.
              </Text>
            </Box>
            <Badge size="lg" variant="light" color="blue" radius="md">
              Hỗ trợ JPG, PNG, WEBP, GIF, PDF, WEBM, MP4
            </Badge>
          </Group>
        </Stack>

        {/* Dropzone Kéo thả */}
        <Box
          onDragOver={(e) => {
            e.preventDefault();
            setIsDragOver(true);
          }}
          onDragLeave={() => setIsDragOver(false)}
          onDrop={handleDrop}
          style={{
            border: `2px dashed ${isDragOver ? "#228be6" : "#ced4da"}`,
            backgroundColor: isDragOver ? "#f8f9fa" : "#fafbfc",
            borderRadius: 12,
            padding: "36px 20px",
            textAlign: "center",
            cursor: "pointer",
            transition: "all 0.2s ease",
          }}
        >
          <Stack align="center" gap="sm">
            <Box
              style={{
                width: 60,
                height: 60,
                borderRadius: "50%",
                background: "#e7f5ff",
                color: "#1c7ed6",
                display: "flex",
                alignItems: "center",
                justifyContent: "center",
              }}
            >
              <IconFolder size={32} />
            </Box>
            <Box>
              <Text fw={600} size="md">
                Kéo & thả cả thư mục (folder) hoặc nhiều ảnh/video/PDF vào đây
              </Text>
              <Text size="xs" c="dimmed" mt={4}>
                Hỗ trợ hình ảnh, video (WEBM, MP4) và tài liệu PDF
              </Text>
            </Box>

            <Group gap="sm" mt="xs" wrap="wrap" justify="center">
              <Button
                leftSection={<IconFolder size={18} />}
                variant="filled"
                color="blue"
                size="sm"
                radius="md"
                disabled={loading}
                onClick={(e) => {
                  e.stopPropagation();
                  folderInputRef.current?.click();
                }}
              >
                Chọn thư mục (Folder)
              </Button>
              <Button
                leftSection={<IconPhoto size={18} />}
                variant="outline"
                color="blue"
                size="sm"
                radius="md"
                disabled={loading}
                onClick={(e) => {
                  e.stopPropagation();
                  fileInputRef.current?.click();
                }}
              >
                Chọn tệp lẻ (Ảnh, Video)
              </Button>
              <Button
                leftSection={<IconLink size={18} />}
                variant="light"
                color="indigo"
                size="sm"
                radius="md"
                disabled={loading}
                onClick={(e) => {
                  e.stopPropagation();
                  setUrlFetchError(null);
                  setUrlModalOpened(true);
                }}
              >
                Tải từ liên kết (URL)
              </Button>
            </Group>
          </Stack>
        </Box>

        {/* Báo lỗi nếu có */}
        {error && (
          <Alert
            icon={<IconX size={18} />}
            color="red"
            radius="md"
            mt="md"
            withCloseButton
            onClose={() => setError(null)}
          >
            {error}
          </Alert>
        )}

        {/* Danh sách tệp đang chờ tải lên */}
        {selectedFiles.length > 0 && (
          <Box mt="lg">
            <Divider mb="md" />
            <Group justify="space-between" mb="xs">
              <Group gap="xs">
                <Text fw={600} size="sm">
                  Đã chọn:{" "}
                  <Text span c="blue" fw={700}>
                    {selectedFiles.length} tệp
                  </Text>
                </Text>
                {folderName && (
                  <Badge variant="dot" color="teal">
                    Thư mục: {folderName}
                  </Badge>
                )}
                <Badge variant="outline" color="gray">
                  Tổng:{" "}
                  {formatFileSize(
                    selectedFiles.reduce((acc, curr) => acc + curr.size, 0)
                  )}
                </Badge>
              </Group>

              <Group gap="xs">
                <Button
                  variant="subtle"
                  color="red"
                  size="xs"
                  leftSection={<IconTrash size={14} />}
                  onClick={handleClearSelected}
                  disabled={loading}
                >
                  Xóa chọn lại
                </Button>
                <Button
                  leftSection={<IconUpload size={16} />}
                  onClick={handleUpload}
                  loading={loading}
                  color="green"
                  size="sm"
                  radius="md"
                >
                  Bắt đầu tải lên CDN
                </Button>
              </Group>
            </Group>

            {/* Thanh tiến trình khi đang upload */}
            {loading && (
              <Box my="sm">
                <Progress
                  value={uploadProgress}
                  size="lg"
                  radius="xl"
                  animated
                  color="blue"
                />
                <Text size="xs" c="dimmed" mt={4} ta="center">
                  {uploadStatusText} ({uploadProgress}%)
                </Text>
              </Box>
            )}

            {/* Danh sách preview nhỏ gọn cuộn được */}
            <Paper
              withBorder
              radius="md"
              p="xs"
              style={{
                maxHeight: 180,
                overflowY: "auto",
                backgroundColor: "#fbfcfd",
              }}
            >
              <SimpleGrid cols={{ base: 1, sm: 2, md: 3 }} spacing="xs">
                {selectedFiles.slice(0, 30).map((item, idx) => (
                  <Group
                    key={idx}
                    justify="space-between"
                    p={6}
                    style={{
                      borderRadius: 6,
                      background: "#fff",
                      border: "1px solid #f1f3f5",
                    }}
                  >
                    <Group gap="xs" style={{ overflow: "hidden" }}>
                      {item.previewUrl ? (
                        isVideoFile(item.file.name) || item.file.type.startsWith("video/") ? (
                          <Box
                            style={{
                              width: 28,
                              height: 28,
                              borderRadius: 4,
                              overflow: "hidden",
                              backgroundColor: "#1a1b1e",
                              display: "flex",
                              alignItems: "center",
                              justifyContent: "center",
                              flexShrink: 0,
                            }}
                          >
                            <video
                              src={item.previewUrl}
                              autoPlay
                              loop
                              muted
                              playsInline
                              style={{ width: "100%", height: "100%", objectFit: "cover" }}
                            />
                          </Box>
                        ) : (
                          <Image
                            src={item.previewUrl}
                            width={28}
                            height={28}
                            radius={4}
                            fit="cover"
                          />
                        )
                      ) : (
                        <IconFileTypePdf size={24} color="#e03131" />
                      )}
                      <Box style={{ overflow: "hidden" }}>
                        <Text size="xs" fw={500} truncate title={item.relativePath}>
                          {item.relativePath}
                        </Text>
                        <Text size="10px" c="dimmed">
                          {formatFileSize(item.size)}
                        </Text>
                      </Box>
                    </Group>
                    <ActionIcon
                      size="xs"
                      variant="subtle"
                      color="gray"
                      disabled={loading}
                      onClick={() => handleRemoveFile(idx)}
                    >
                      <IconX size={12} />
                    </ActionIcon>
                  </Group>
                ))}
              </SimpleGrid>
              {selectedFiles.length > 30 && (
                <Text size="xs" c="dimmed" ta="center" mt={6}>
                  ...và còn {selectedFiles.length - 30} tệp khác
                </Text>
              )}
            </Paper>
          </Box>
        )}
      </Paper>

      {/* Khu vực Kết quả & Tải file chứa toàn bộ CDN */}
      {uploadedItems.length > 0 && (
        <Paper
          shadow="xs"
          p="xl"
          radius="lg"
          style={{
            background: "#fff",
            border: "1px solid #e9ecef",
          }}
        >
          {/* Header kết quả + Các nút Xuất file */}
          <Group justify="space-between" align="center" mb="md" wrap="wrap">
            <Box>
              <Group gap="xs">
                <Text size="lg" fw={700} c="dark.8">
                  Tệp đã tải lên CDN
                </Text>
                <Badge size="lg" color="green" variant="light" radius="md">
                  {uploadedItems.length} tệp sẵn sàng
                </Badge>
              </Group>
              <Text size="xs" c="dimmed" mt={2}>
                Toàn bộ đường dẫn CDN đã được tạo hoàn chỉnh và chuyển đổi theo domain CDN.
              </Text>
            </Box>

            {/* Bộ 3 công cụ xuất danh sách CDN */}
            <Group gap="xs">
              <Tooltip label="Sao chép toàn bộ link CDN vào bộ nhớ tạm">
                <Button
                  leftSection={<IconCopy size={16} />}
                  variant="light"
                  color="blue"
                  size="sm"
                  radius="md"
                  onClick={handleCopyAllUrls}
                >
                  Sao chép tất cả CDN
                </Button>
              </Tooltip>

              <Tooltip label="Tải danh sách CDN thành file Text (.txt)">
                <Button
                  leftSection={<IconFileText size={16} />}
                  variant="outline"
                  color="gray"
                  size="sm"
                  radius="md"
                  onClick={handleExportTxt}
                >
                  Xuất File Text
                </Button>
              </Tooltip>

              <Tooltip label="Tải file Excel (.xlsx) chứa đầy đủ tên file và link CDN tương ứng">
                <Button
                  leftSection={<IconFileSpreadsheet size={16} />}
                  variant="filled"
                  color="teal"
                  size="sm"
                  radius="md"
                  onClick={handleExportExcel}
                >
                  Tải Folder CDN (Excel)
                </Button>
              </Tooltip>
            </Group>
          </Group>

          {/* Thanh công cụ tìm kiếm và chuyển chế độ xem */}
          <Group justify="space-between" mb="md" wrap="wrap">
            <TextInput
              placeholder="Tìm theo tên file hoặc link CDN..."
              leftSection={<IconSearch size={16} />}
              value={searchQuery}
              onChange={(e) => setSearchQuery(e.target.value)}
              size="xs"
              radius="md"
              style={{ minWidth: 260 }}
            />

            <Group gap="xs">
              <SegmentedControl
                size="xs"
                radius="md"
                value={viewMode}
                onChange={(val: any) => setViewMode(val)}
                data={[
                  {
                    value: "grid",
                    label: (
                      <Group gap={4}>
                        <IconLayoutGrid size={14} />
                        <span>Lưới ảnh</span>
                      </Group>
                    ),
                  },
                  {
                    value: "table",
                    label: (
                      <Group gap={4}>
                        <IconList size={14} />
                        <span>Bảng chi tiết</span>
                      </Group>
                    ),
                  },
                ]}
              />

              <Button
                variant="subtle"
                color="red"
                size="xs"
                leftSection={<IconTrash size={14} />}
                onClick={() => setUploadedItems([])}
              >
                Xóa lịch sử
              </Button>
            </Group>
          </Group>

          <Divider mb="md" />

          {/* DẠNG 1: HIỂN THỊ DẠNG BẢNG (TABLE) - Rất tiện cho số lượng lớn */}
          {viewMode === "table" ? (
            <Box style={{ overflowX: "auto" }}>
              <Table striped highlightOnHover withTableBorder withColumnBorders>
                <Table.Thead>
                  <Table.Tr style={{ backgroundColor: "#f8f9fa" }}>
                    <Table.Th style={{ width: 50, textAlign: "center" }}>STT</Table.Th>
                    <Table.Th style={{ width: 60, textAlign: "center" }}>Ảnh</Table.Th>
                    <Table.Th>Tên tệp gốc / Thư mục</Table.Th>
                    <Table.Th>Đường dẫn CDN</Table.Th>
                    <Table.Th style={{ width: 100, textAlign: "center" }}>Thao tác</Table.Th>
                  </Table.Tr>
                </Table.Thead>
                <Table.Tbody>
                  {filteredItems.map((item, idx) => {
                    const rawUrl = getFileUrl(item);
                    const fullUrl = convertImageDomain(rawUrl);
                    const displayName = getFileName(item);
                    const isPdf =
                      isPdfFile(item.pathNameBefore) || isPdfFile(rawUrl) || isPdfFile(item.originalName);
                    const isVideo =
                      isVideoFile(item.pathNameBefore) || isVideoFile(rawUrl) || isVideoFile(item.originalName);

                    return (
                      <Table.Tr key={idx}>
                        <Table.Td style={{ textAlign: "center", fontSize: 13 }}>
                          {idx + 1}
                        </Table.Td>
                        <Table.Td style={{ textAlign: "center" }}>
                          {isPdf ? (
                            <IconFileTypePdf size={28} color="#e03131" />
                          ) : isVideo ? (
                            <Box
                              style={{
                                width: 34,
                                height: 34,
                                borderRadius: 4,
                                overflow: "hidden",
                                backgroundColor: "#1a1b1e",
                                display: "inline-flex",
                                alignItems: "center",
                                justifyContent: "center",
                              }}
                            >
                              <video
                                src={fullUrl}
                                autoPlay
                                loop
                                muted
                                playsInline
                                style={{ width: "100%", height: "100%", objectFit: "cover" }}
                              />
                            </Box>
                          ) : (
                            <Image
                              src={fullUrl}
                              width={32}
                              height={32}
                              radius={4}
                              fit="cover"
                              fallbackSrc="https://placehold.co/32x32?text=NA"
                            />
                          )}
                        </Table.Td>
                        <Table.Td>
                          <Text size="xs" fw={600} title={displayName}>
                            {displayName}
                          </Text>
                          {item.size ? (
                            <Text size="10px" c="dimmed">
                              {formatFileSize(item.size)}
                            </Text>
                          ) : null}
                        </Table.Td>
                        <Table.Td>
                          <Text
                            size="xs"
                            c="blue.7"
                            style={{
                              fontFamily: "monospace",
                              cursor: "pointer",
                              userSelect: "all",
                              wordBreak: "break-all",
                            }}
                            title="Click để copy link"
                            onClick={async () => {
                              try {
                                await navigator.clipboard.writeText(fullUrl);
                                NotificationExtension.Success("Đã sao chép link CDN!");
                              } catch {}
                            }}
                          >
                            {fullUrl}
                          </Text>
                        </Table.Td>
                        <Table.Td style={{ textAlign: "center" }}>
                          <Group gap={4} justify="center">
                            <Tooltip label="Sao chép link" withArrow>
                              <ActionIcon
                                size="sm"
                                variant="light"
                                color="blue"
                                onClick={async () => {
                                  try {
                                    await navigator.clipboard.writeText(fullUrl);
                                    NotificationExtension.Success(
                                      "Đã sao chép link CDN!"
                                    );
                                  } catch {}
                                }}
                              >
                                <IconCopy size={14} />
                              </ActionIcon>
                            </Tooltip>
                            <Tooltip label="Mở xem tệp" withArrow>
                              <ActionIcon
                                size="sm"
                                variant="light"
                                color="gray"
                                component="a"
                                href={fullUrl}
                                target="_blank"
                                rel="noopener noreferrer"
                              >
                                <IconExternalLink size={14} />
                              </ActionIcon>
                            </Tooltip>
                          </Group>
                        </Table.Td>
                      </Table.Tr>
                    );
                  })}
                </Table.Tbody>
              </Table>
            </Box>
          ) : (
            /* DẠNG 2: HIỂN THỊ DẠNG LƯỚI (GRID CARDS) */
            <SimpleGrid cols={{ base: 2, sm: 3, md: 4 }} spacing="md">
              {filteredItems.map((item, idx) => {
                const rawUrl = getFileUrl(item);
                const fullUrl = convertImageDomain(rawUrl);
                const isPdf =
                  isPdfFile(item.pathNameBefore) || isPdfFile(rawUrl) || isPdfFile(item.originalName);
                const isVideo =
                  isVideoFile(item.pathNameBefore) || isVideoFile(rawUrl) || isVideoFile(item.originalName);
                const displayName = getFileName(item);

                return (
                  <Card
                    key={idx}
                    withBorder
                    padding="xs"
                    radius="md"
                    style={{
                      backgroundColor: "#fafbfc",
                      transition: "all 0.15s ease",
                    }}
                  >
                    <Box
                      style={{
                        position: "relative",
                        width: "100%",
                        height: 130,
                        backgroundColor: isVideo ? "#141517" : "#f1f3f5",
                        borderRadius: 6,
                        overflow: "hidden",
                        display: "flex",
                        alignItems: "center",
                        justifyContent: "center",
                      }}
                    >
                      {isPdf ? (
                        <Stack align="center" gap={4}>
                          <IconFileTypePdf size={44} color="#e03131" stroke={1.5} />
                          <Badge color="red" variant="filled" size="xs">
                            PDF
                          </Badge>
                        </Stack>
                      ) : isVideo ? (
                        <>
                          <video
                            src={fullUrl}
                            autoPlay
                            loop
                            muted
                            playsInline
                            style={{
                              maxWidth: "100%",
                              maxHeight: 130,
                              objectFit: "contain",
                            }}
                          />
                          <Badge
                            color="indigo"
                            variant="filled"
                            size="xs"
                            style={{
                              position: "absolute",
                              top: 6,
                              left: 6,
                              pointerEvents: "none",
                              zIndex: 1,
                            }}
                          >
                            VIDEO
                          </Badge>
                        </>
                      ) : (
                        <Image
                          src={fullUrl}
                          alt={displayName}
                          fit="cover"
                          height={130}
                          width="100%"
                          fallbackSrc="https://placehold.co/130x130?text=No+Image"
                        />
                      )}
                    </Box>

                    <Text
                      size="xs"
                      mt={8}
                      fw={600}
                      lineClamp={1}
                      title={displayName}
                    >
                      {displayName}
                    </Text>

                    <Text
                      size="11px"
                      c="dimmed"
                      mt={2}
                      lineClamp={1}
                      title={fullUrl}
                      style={{
                        cursor: "pointer",
                        fontFamily: "monospace",
                        userSelect: "all",
                      }}
                      onDoubleClick={async () => {
                        try {
                          await navigator.clipboard.writeText(fullUrl);
                          NotificationExtension.Success("Đã sao chép link CDN!");
                        } catch {}
                      }}
                    >
                      {fullUrl}
                    </Text>

                    <Group justify="space-between" mt={8}>
                      <Badge size="xs" variant="outline" color="gray">
                        {item.size ? formatFileSize(item.size) : "CDN"}
                      </Badge>
                      <Group gap={4}>
                        <Tooltip label="Sao chép link" withArrow>
                          <ActionIcon
                            size="sm"
                            variant="light"
                            color="blue"
                            onClick={async () => {
                              try {
                                await navigator.clipboard.writeText(fullUrl);
                                NotificationExtension.Success(
                                  "Đã sao chép link CDN!"
                                );
                              } catch {}
                            }}
                          >
                            <IconCopy size={13} />
                          </ActionIcon>
                        </Tooltip>
                        <Tooltip label="Mở xem tệp" withArrow>
                          <ActionIcon
                            size="sm"
                            variant="light"
                            color="gray"
                            component="a"
                            href={fullUrl}
                            target="_blank"
                            rel="noopener noreferrer"
                          >
                            <IconExternalLink size={13} />
                          </ActionIcon>
                        </Tooltip>
                      </Group>
                    </Group>
                  </Card>
                );
              })}
            </SimpleGrid>
          )}
        </Paper>
      )}
    </Box>
  );
};

export default UploadImageOnline;
